import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAppDispatch } from '../../../store/store';
import { useSelector } from 'react-redux';
import moment from 'moment';
import IssueLobbyingDashboard from '../UI/IssueLobbyingDashboard';
import {
    selectDPOHCluster,
    selectDPOHClusterLoading,
    selectFirmCluster,
    selectFirmClusterLoading,
    selectInstitutionCluster,
    selectInstitutionClusterLoading,
    selectIssueInfo,
    selectIssueInfoLoading,
    selectIssueRecentLobbying,
    selectIssueRecentLobbyingLoading,
    selectIssueRegistrations,
    selectIssueRegistrationsLoading,
    selectIssueSentences,
    selectIssueSentencesLoading,
    selectOrganizationCluster,
    selectOrganizationClusterLoading,
    selectSubjectCluster,
    selectSubjectClusterLoading,
    selectIssueExportFail,
    selectIssueExportLoading,
    selectIssueUpdates,
    selectIssueUpdatesLoading,
    selectIssueKeywordMentions,
    selectIssueKeywordMentionsLoading,
    selectSectorCluster,
    selectSectorClusterLoading,
    selectMentionCluster,
    selectMentionClusterLoading,
    selectIssueTweets,
    selectIssueTweetsLoading,
    selectIssueMpSocialOverview,
    selectIssueMpSocialOverviewLoading,
    selectIssueGovernmentAnnouncements,
    selectIssueGovernmentAnnouncementsLoading,
    selectIssueSummary,
    selectIssueSummaryLoading,
    selectGazette,
    selectGazetteLoading,
    selectIssueSubjectMatterDetailsSummary,
    selectIssueSubjectMatterDetailsSummaryLoading,
    selectIssueOrgActivity,
    selectIssueOrgActivityLoading,
    selectIssueDpohActivity,
    selectIssueDpohActivityLoading,
    selectIssueMpActivity,
    selectIssueMpActivityLoading,
    selectIssueSenatorSocialOverview,
    selectIssueSenatorSocialOverviewLoading,
    selectIssueTopSectorActivity,
    selectIssueTopSectorActivityLoading,
    selectIssueAllSectorActivity,
    selectIssueAllSectorActivityLoading,
    selectIssueTopEntities,
    selectIssueTopEntitiesLoading,
} from '../store/issueSlice';
import { ProductType, selectUserPermissions } from '../../../store/reducers/userSlice';
import { SubscriptionRequired } from '../../../pages/misc/SubscriptionRequired';
import { entityCluster, entityRegistration } from '../../../interfaces/generic-entity.interface';
import { SectorGovernmentInstitutions } from '../../sector-dashboards/properties/SectorGovernmentInstitutions';
import { SectorPublicOffice } from '../../sector-dashboards/properties/SectorPublicOffice';
import { SectorSubjects } from '../../sector-dashboards/properties/SectorSubjects';
import { SectorLobbyFirms } from '../../sector-dashboards/properties/SectorLobbyFirms';
import {
    governmentAnnouncement,
    IssueGazette,
    issueInfo,
    keywordMentions,
    Tweet,
} from '../interface/issue-entity.interface';
import { recentUpdates } from '../../shared/interfaces/whats-new.interface';
import { IssueMentions, IssueOrganizations, IssueSectors } from '../properties/Tooltips';
import IssueParlDashboard from '../UI/IssueParlDashboard';
import { IssueDispatches } from './IssueDispatches';
import { RangedSummary } from 'features/shared/interfaces/text-summary.interface';
import { ConfigDescriptor } from 'features/config/feature-configs/ConfigDescriptor';
import { EntityType } from 'features/config/Entities';
import {
    CorrelatedEntityCommunication,
    EntityCommunicationActivity,
    EntityCommunicationActivityBreakdown,
    EntityCommunicationMonthlyAggregate,
} from 'interfaces/entity-communication.interface';

const issueEntityConfig = ConfigDescriptor.getEntityConfig(EntityType.Issues).entityDashboardConfig;

const IssueDataLayer = () => {
    const dispatch = useAppDispatch();

    // Get the id of the current entity
    const urlParams = useParams();
    const entityCode = urlParams.entityId !== undefined ? urlParams.entityId : '';

    const [date, setDate] = useState<string>(moment(new Date()).format('YYYY-MM-DD'));
    const [displayLobbyingData, setDisplayLobbyingData] = useState<boolean>(false);
    const [sharedDataFetched, setSharedDataFetched] = useState<boolean>(false);
    const [lobbyingDataFetched, setLobbyingDataFetched] = useState<boolean>(false);
    const [parliamentaryDataFetched, setParliamentaryDataFetched] = useState<boolean>(false);

    const prevDateRef = useRef<string>('');
    const prevEntityCodeRef = useRef<string>('');

    const announcements: governmentAnnouncement[] | null = useSelector(
        selectIssueGovernmentAnnouncements
    );
    const announcementsLoading: boolean = useSelector(selectIssueGovernmentAnnouncementsLoading);

    const info: issueInfo | null = useSelector(selectIssueInfo);
    const infoLoading: boolean = useSelector(selectIssueInfoLoading);

    const subjectMatterSummary: RangedSummary | null = useSelector(
        selectIssueSubjectMatterDetailsSummary
    );
    const subjectMatterSummaryLoading: boolean = useSelector(
        selectIssueSubjectMatterDetailsSummaryLoading
    );

    const mpSocialOverview: any[] | null = useSelector(selectIssueMpSocialOverview);
    const mpSocialOverviewLoading: boolean = useSelector(selectIssueMpSocialOverviewLoading);

    const senatorSocialOverview: any[] | null = useSelector(selectIssueSenatorSocialOverview);
    const senatorSocialOverviewLoading: boolean = useSelector(
        selectIssueSenatorSocialOverviewLoading
    );

    const updates: recentUpdates | null = useSelector(selectIssueUpdates);
    const updatesLoading: boolean = useSelector(selectIssueUpdatesLoading);

    const tweets: Tweet[] | null = useSelector(selectIssueTweets);
    const tweetsLoading: boolean = useSelector(selectIssueTweetsLoading);

    const keywordMention: keywordMentions[] | null = useSelector(selectIssueKeywordMentions);
    const keywordMentionLoading: boolean = useSelector(selectIssueKeywordMentionsLoading);

    const sentences: any[] | null = useSelector(selectIssueSentences);
    const sentencesLoading: boolean = useSelector(selectIssueSentencesLoading);

    const activity: CorrelatedEntityCommunication[] | null = useSelector(selectIssueRecentLobbying);
    const activityLoading: boolean = useSelector(selectIssueRecentLobbyingLoading);

    const registrations: entityRegistration[] | null = useSelector(selectIssueRegistrations);
    const registrationsLoading: boolean = useSelector(selectIssueRegistrationsLoading);

    const topEntities = useSelector(selectIssueTopEntities);
    const topEntitiesLoading = useSelector(selectIssueTopEntitiesLoading);

    const allSectorActivity: EntityCommunicationMonthlyAggregate[] | null = useSelector(
        selectIssueAllSectorActivity
    );
    const allSectorActivityLoading: boolean = useSelector(selectIssueAllSectorActivityLoading);

    const topSectorActivity: EntityCommunicationActivityBreakdown[] | null = useSelector(
        selectIssueTopSectorActivity
    );
    const topSectorActivityLoading: boolean = useSelector(selectIssueTopSectorActivityLoading);

    const orgActivity: EntityCommunicationActivity[] | null = useSelector(selectIssueOrgActivity);
    const orgActivityLoading: boolean = useSelector(selectIssueOrgActivityLoading);

    const dpohActivity: EntityCommunicationActivity[] | null = useSelector(selectIssueDpohActivity);
    const dpohActivityLoading: boolean = useSelector(selectIssueDpohActivityLoading);

    const mpActivity: EntityCommunicationActivity[] | null = useSelector(selectIssueMpActivity);
    const mpActivityLoading: boolean = useSelector(selectIssueMpActivityLoading);

    const mentionCluster: entityCluster | null = useSelector(selectMentionCluster);
    const mentionClusterLoading: boolean = useSelector(selectMentionClusterLoading);

    const orgCluster: entityCluster | null = useSelector(selectOrganizationCluster);
    const orgClusterLoading: boolean = useSelector(selectOrganizationClusterLoading);

    const sectorCluster: entityCluster | null = useSelector(selectSectorCluster);
    const sectorClusterLoading: boolean = useSelector(selectSectorClusterLoading);

    const institutionCluster: entityCluster | null = useSelector(selectInstitutionCluster);
    const institutionClusterLoading: boolean = useSelector(selectInstitutionClusterLoading);

    const firmCluster: entityCluster | null = useSelector(selectFirmCluster);
    const firmClusterLoading: boolean = useSelector(selectFirmClusterLoading);

    const dpohCluster: entityCluster | null = useSelector(selectDPOHCluster);
    const dpohClusterLoading: boolean = useSelector(selectDPOHClusterLoading);

    const subjectCluster: entityCluster | null = useSelector(selectSubjectCluster);
    const subjectClusterLoading: boolean = useSelector(selectSubjectClusterLoading);

    const issue_summary = useSelector(selectIssueSummary);
    const issue_summaryLoading = useSelector(selectIssueSummaryLoading);

    const gazette: IssueGazette | null = useSelector(selectGazette);
    const gazetteLoading: boolean = useSelector(selectGazetteLoading);

    const export_fail = useSelector(selectIssueExportFail);
    const exportLoading = useSelector(selectIssueExportLoading);

    const permissions = useSelector(selectUserPermissions);

    useEffect(() => {
        const isDateChanged = prevDateRef.current !== date;
        const isEntityChanged = prevEntityCodeRef.current !== entityCode;
        if (permissions?.issues !== undefined || permissions?.productType === ProductType.TRIAL) {
            // Shared dispatches
            if (!sharedDataFetched || isDateChanged || isEntityChanged) {
                IssueDispatches.issueSharedDispatches(dispatch, entityCode, date);
                setSharedDataFetched(true);
            }
            // Lobbying dispatches
            if (!lobbyingDataFetched || isDateChanged || isEntityChanged) {
                IssueDispatches.issueLobbyingDispatches(dispatch, entityCode, date);
                setLobbyingDataFetched(true);
            }
            // Parliamentary dispatches
            if (!parliamentaryDataFetched || isDateChanged || isEntityChanged) {
                IssueDispatches.issueParliamentaryDispatches(dispatch, entityCode, date);
                setParliamentaryDataFetched(true);
            }
            prevDateRef.current = date;
            prevEntityCodeRef.current = entityCode;
        }
    }, [
        dispatch,
        entityCode,
        date,
        permissions,
        lobbyingDataFetched,
        parliamentaryDataFetched,
        sharedDataFetched,
    ]);

    return permissions?.issues || permissions?.productType === ProductType.TRIAL ? (
        <div className="flex flex-col gap-4">
            {displayLobbyingData ? (
                <IssueLobbyingDashboard
                    displayLobbyingData={displayLobbyingData}
                    setDisplayLobbyingData={setDisplayLobbyingData}
                    date={date}
                    handleDateChange={setDate}
                    info={info}
                    infoLoading={infoLoading}
                    subjectMatterSummary={subjectMatterSummary}
                    subjectMatterSummaryLoading={subjectMatterSummaryLoading}
                    updates={updates}
                    updatesLoading={updatesLoading}
                    overview={allSectorActivity}
                    overviewLoading={allSectorActivityLoading}
                    recentLobbying={activity}
                    recentLobbyingLoading={activityLoading}
                    recentRegistrations={registrations}
                    recentRegistrationsLoading={registrationsLoading}
                    topSectorActivity={topSectorActivity}
                    topSectorActivityLoading={topSectorActivityLoading}
                    topEntities={topEntities}
                    topEntitiesLoading={topEntitiesLoading}
                    orgActivity={orgActivity}
                    orgActivityLoading={orgActivityLoading}
                    dpohActivity={dpohActivity}
                    dpohActivityLoading={dpohActivityLoading}
                    mpActivity={mpActivity}
                    mpActivityLoading={mpActivityLoading}
                    clusters={[
                        {
                            cluster_data: sectorCluster,
                            title: 'Sectors Lobbying on Issue',
                            tooltipContent: IssueSectors,
                            loading: sectorClusterLoading,
                        },
                        {
                            cluster_data: orgCluster,
                            title: 'Organizations Lobbying on Issue',
                            tooltipContent: IssueOrganizations,
                            loading: orgClusterLoading,
                        },
                        {
                            cluster_data: institutionCluster,
                            title: 'Institutions Lobbied on Issue',
                            tooltipContent: SectorGovernmentInstitutions,
                            loading: institutionClusterLoading,
                        },
                        {
                            cluster_data: dpohCluster,
                            title: 'DPOHs Lobbied on Issue',
                            tooltipContent: SectorPublicOffice,
                            loading: dpohClusterLoading,
                        },
                        {
                            cluster_data: subjectCluster,
                            title: 'Subjects of Lobbying Communications',
                            tooltipContent: SectorSubjects,
                            loading: subjectClusterLoading,
                        },
                        {
                            cluster_data: firmCluster,
                            title: 'Lobby Firms',
                            tooltipContent: SectorLobbyFirms,
                            loading: firmClusterLoading,
                        },
                    ]}
                    exportFail={export_fail}
                    exportLoading={exportLoading}
                    isExportVisible={issueEntityConfig.isSavePDFEnabled}
                />
            ) : (
                <IssueParlDashboard
                    displayLobbyingData={displayLobbyingData}
                    setDisplayLobbyingData={setDisplayLobbyingData}
                    date={date}
                    handleDateChange={setDate}
                    info={info}
                    infoLoading={infoLoading}
                    announcements={announcements}
                    announcementsLoading={announcementsLoading}
                    updates={updates}
                    updatesLoading={updatesLoading}
                    issueSummary={issue_summary}
                    issueSummaryLoading={issue_summaryLoading}
                    tweets={tweets}
                    tweetsLoading={tweetsLoading}
                    keywordMentions={keywordMention}
                    keywordMentionsLoading={keywordMentionLoading}
                    mpSocialOverview={mpSocialOverview}
                    mpSocialOverviewLoading={mpSocialOverviewLoading}
                    senatorSocialOverview={senatorSocialOverview}
                    senatorSocialOverviewLoading={senatorSocialOverviewLoading}
                    bumperData={sentences}
                    bumperDataLoading={sentencesLoading}
                    gazetteData={gazette}
                    gazetteLoading={gazetteLoading}
                    exportFail={export_fail}
                    exportLoading={exportLoading}
                    isExportVisible={issueEntityConfig.isSavePDFEnabled}
                    clusters={[
                        {
                            cluster_data: mentionCluster,
                            title: 'MP Mentions of issue',
                            tooltipContent: IssueMentions,
                            loading: mentionClusterLoading,
                        },
                    ]}
                />
            )}
        </div>
    ) : (
        <SubscriptionRequired />
    );
};

export default IssueDataLayer;
